"use client";

import type { CSSProperties } from "react";

import { ScrollArea } from "@/components/ui/scroll-area";
import { PROFILE_SIDEBAR_WIDTH } from "@/features/candidate/lib/profile-constants";

import {
  SearchHeaderSkeleton,
  PipelineHeaderSkeleton,
} from "./header-skeleton";
import { SpotlightSkeleton } from "./sidebar/spotlight-skeleton";
import { HighlightsSkeleton } from "./sidebar/highlights-skeleton";
import { NotesSkeleton } from "./sidebar/notes-skeleton";
import {
  PersonalInfoSkeleton,
  AboutSkeleton,
  ExperienceSkeleton,
  EducationSkeleton,
  CertificationsSkeleton,
  SkillsSkeleton,
  LanguagesSkeleton,
} from "./sections/skeletons";

function SkeletonBar({
  className,
  style,
}: {
  className: string;
  style?: CSSProperties;
}) {
  return (
    <div
      className={`rounded bg-contrast/6 shimmer shimmer-bg text-foreground ${className}`}
      style={style}
    />
  );
}

function MobileActionStripSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-border bg-surface-header px-4 py-3 md:hidden">
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonBar key={i} className="h-7 flex-1 !rounded-md" />
      ))}
    </div>
  );
}

function MainColumnSkeleton({
  mobileSidebarBelow,
}: {
  mobileSidebarBelow?: boolean;
}) {
  return (
    <ScrollArea
      constrainContentWidth
      className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain bg-surface-elevated"
    >
      {/* Tabs */}
      <div className="px-4 py-4 md:px-8 md:py-6">
        <div className="w-full overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex w-fit items-start gap-1 rounded-lg bg-contrast/3 p-[3px]">
            {["Profile", "Jobs", "Sequences", "Activities"].map((t) => (
              <SkeletonBar
                key={t}
                className="h-7 !rounded-md"
                style={{ width: t.length * 10 + 50 }}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-6 px-4 pb-8 md:px-8">
        <PersonalInfoSkeleton />
        <AboutSkeleton />
        <ExperienceSkeleton />
        <EducationSkeleton />
        <CertificationsSkeleton />
        <SkillsSkeleton />
        <LanguagesSkeleton />
      </div>

      {/* Mobile-only sidebar sections rendered inline */}
      {mobileSidebarBelow && (
        <div className="md:hidden border-t border-border">
          <SpotlightSkeleton />
          <HighlightsSkeleton />
          <NotesSkeleton />
        </div>
      )}
    </ScrollArea>
  );
}

function PipelineStageControlsSkeleton() {
  return (
    <div className="flex flex-col gap-2.5 border-b border-border px-8 py-6">
      <div className="flex items-start gap-3">
        <SkeletonBar className="flex-1 h-7 !rounded-[6px]" />
        <SkeletonBar className="flex-1 h-7 !rounded-[6px]" />
      </div>
      <SkeletonBar className="h-7 w-full !rounded-[6px]" />
    </div>
  );
}

export function ProfileSheetSkeleton({
  variant = "search",
}: {
  variant?: "search" | "pipeline" | "task";
}) {
  // The task profile view renders the candidate as a single column (no
  // header chrome — the task view owns the header — and no right sidebar
  // rail). Render just the main section column for that variant.
  if (variant === "task") {
    return <MainColumnSkeleton />;
  }

  const isPipeline = variant === "pipeline";

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-surface-elevated">
      {isPipeline ? <PipelineHeaderSkeleton /> : <SearchHeaderSkeleton />}

      <MobileActionStripSkeleton count={isPipeline ? 3 : 2} />

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden md:flex-row">
        <MainColumnSkeleton mobileSidebarBelow />

        {/* Desktop right sidebar */}
        <ScrollArea
          constrainContentWidth
          className="hidden md:flex min-h-0 shrink-0 flex-col border-l border-border bg-surface-elevated"
          style={{ width: PROFILE_SIDEBAR_WIDTH }}
        >
          {isPipeline && <PipelineStageControlsSkeleton />}
          <SpotlightSkeleton />
          <HighlightsSkeleton />
          <NotesSkeleton />
        </ScrollArea>
      </div>
    </div>
  );
}
